import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, Trash2, PlayCircle, Copy, Eye, EyeOff, Key, RefreshCw, XCircle } from "lucide-react";
import { useApiEndpoint, useUpdateApiEndpoint, useManageApiEndpointToken } from "@/hooks/use-api-endpoint";
import { useOrganizationDevices } from "@/hooks/use-organization-devices";
import { useTestApiEndpoint } from "@/hooks/use-test-api-endpoint";
import { toast } from "sonner";
interface APIEndpointSectionProps {
  organizationId: string;
}
export const APIEndpointSection: React.FC<APIEndpointSectionProps> = ({
  organizationId
}) => {
  const [endpointUrl, setEndpointUrl] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [showToken, setShowToken] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'regenerate' | 'revoke' | null>(null);
  const {
    data: endpointInfo,
    isLoading,
    error
  } = useApiEndpoint(organizationId);
  const {
    data: organizationDevices,
    isLoading: devicesLoading
  } = useOrganizationDevices();
  const updateEndpointMutation = useUpdateApiEndpoint();
  const testEndpointMutation = useTestApiEndpoint();
  const tokenMutation = useManageApiEndpointToken();

  // Update local state when data is loaded
  useEffect(() => {
    if (endpointInfo) {
      setEndpointUrl(endpointInfo.url || '');
      setIsEnabled(endpointInfo.enabled);
      setHasChanges(false);
    }
  }, [endpointInfo]);

  // Track changes
  useEffect(() => {
    if (endpointInfo) {
      const urlChanged = endpointUrl !== (endpointInfo.url || '');
      const enabledChanged = isEnabled !== endpointInfo.enabled;
      setHasChanges(urlChanged || enabledChanged);
    }
  }, [endpointUrl, isEnabled, endpointInfo]);
  const validateUrl = (url: string): boolean => {
    if (!url.trim()) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };
  const handleSave = async () => {
    if (!endpointUrl.trim()) {
      toast.error('Please enter a valid endpoint URL');
      return;
    }
    if (!validateUrl(endpointUrl)) {
      toast.error('Please enter a valid HTTP/HTTPS URL');
      return;
    }
    try {
      await updateEndpointMutation.mutateAsync({
        organizationId,
        endpointAddress: endpointUrl.trim(),
        enableEndpoint: isEnabled
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to update endpoint:', error);
    }
  };
  const handleClear = () => {
    setEndpointUrl('');
    setIsEnabled(false);
  };
  const handleToggleEnabled = (enabled: boolean) => {
    setIsEnabled(enabled);
  };
  const handleTestEndpoint = async () => {
    if (!selectedDeviceId) {
      toast.error('Please select a device to test with');
      return;
    }
    testEndpointMutation.mutate({ deviceId: selectedDeviceId });
  };
  const handleGenerateToken = () => {
    tokenMutation.mutate({
      organizationId,
      action: 'generate'
    });
  };
  const handleRegenerateToken = () => {
    setConfirmAction('regenerate');
  };
  const handleRevokeToken = () => {
    setConfirmAction('revoke');
  };
  const confirmTokenAction = () => {
    if (confirmAction === 'regenerate') {
      tokenMutation.mutate({
        organizationId,
        action: 'generate'
      });
    } else if (confirmAction === 'revoke') {
      tokenMutation.mutate({
        organizationId,
        action: 'revoke'
      });
    }
    setConfirmAction(null);
  };
  const handleCopyToken = () => {
    if (endpointInfo?.endpointToken) {
      navigator.clipboard.writeText(endpointInfo.endpointToken);
      toast.success('Token copied to clipboard');
    }
  };
  const maskToken = (token: string): string => {
    if (!token) return '';
    if (token.length <= 8) return '••••••••';
    return token.slice(0, 4) + '••••••••••••' + token.slice(-4);
  };
  const getTokenStatusBadge = () => {
    const status = endpointInfo?.endpointTokenStatus || 'inactive';
    switch (status) {
      case 'active':
        return <Badge className="bg-[#DFEF97] text-black border-[#DFEF97] hover:bg-[#DFEF97]/90">Active</Badge>;
      case 'revoked':
        return <Badge variant="destructive">Revoked</Badge>;
      default:
        return <Badge variant="secondary">Inactive</Badge>;
    }
  };
  if (isLoading) {
    return <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            API Endpoint
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-10 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>;
  }
  if (error) {
    return <Card>
        <CardHeader>
          <CardTitle>API Endpoint</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">Failed to load endpoint configuration</p>
        </CardContent>
      </Card>;
  }
  const hasActiveToken = endpointInfo?.endpointTokenStatus === 'active' && endpointInfo?.endpointToken;
  return <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Production Webhook Endpoint
          </CardTitle>
          <CardDescription>
            Register an inbound URL where Snapshot will POST device updates to
            you in real time. Use the Endpoint Authentication Token to verify
            each request came from Snapshot.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status Badge */}
          <div className="flex items-center gap-2">
            <Badge className={isEnabled && endpointUrl ? "bg-[#DFEF97] text-black border-[#DFEF97] hover:bg-[#DFEF97]/90" : "bg-red-500 text-white border-red-500 hover:bg-red-500/90"}>
              {isEnabled && endpointUrl ? "Active" : "Inactive"}
            </Badge>
            {endpointInfo?.lastUpdated && <span className="text-sm text-muted-foreground">
                Last updated: {new Date(endpointInfo.lastUpdated).toLocaleDateString()}
              </span>}
          </div>

          {/* URL Input */}
          <div className="space-y-2">
            <Label htmlFor="endpoint-url">Endpoint URL</Label>
            <div className="flex gap-2">
              <Input id="endpoint-url" type="url" placeholder="https://your-api.example.com/webhook" value={endpointUrl} onChange={e => setEndpointUrl(e.target.value)} className="flex-1" />
              <Button variant="outline" size="icon" onClick={handleClear} disabled={!endpointUrl} title="Clear URL">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Enable/Disable Switch */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="endpoint-enabled">Enable Endpoint</Label>
              <p className="text-sm text-muted-foreground">
                When enabled, notifications will be sent to this endpoint
              </p>
            </div>
            <Switch id="endpoint-enabled" checked={isEnabled} onCheckedChange={handleToggleEnabled} disabled={!endpointUrl.trim()} />
          </div>

          {/* URL Validation */}
          {endpointUrl && !validateUrl(endpointUrl) && <p className="text-sm text-destructive">
              Please enter a valid HTTP or HTTPS URL
            </p>}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={!hasChanges || updateEndpointMutation.isPending || !validateUrl(endpointUrl)} className="min-w-24 bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90">
              {updateEndpointMutation.isPending ? <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </> : 'Save Changes'}
            </Button>
          </div>

          {/* Endpoint Token Section */}
          <Separator />

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              <h4 className="text-sm font-medium">Endpoint Authentication Token</h4>
            </div>

            <p className="text-sm text-muted-foreground">
              This token will be included in webhook headers (<code className="bg-muted px-1 rounded">X-Snapshot-Token</code>​header using the Bearer scheme, so you can verify the authenticity of incoming requests.
            </p>

            {/* Token Status and Info */}
            <div className="flex items-center gap-3">
              {getTokenStatusBadge()}
              {endpointInfo?.endpointTokenCreatedAt && endpointInfo.endpointTokenStatus === 'active' && <span className="text-sm text-muted-foreground">
                  Created: {new Date(endpointInfo.endpointTokenCreatedAt).toLocaleDateString()}
                </span>}
            </div>

            {/* Token Display */}
            {hasActiveToken && <div className="flex items-center gap-2">
                <div className="flex-1 bg-muted rounded-md px-3 py-2 font-mono text-sm">
                  {showToken ? endpointInfo.endpointToken : maskToken(endpointInfo.endpointToken || '')}
                </div>
                <Button variant="outline" size="icon" onClick={() => setShowToken(!showToken)} title={showToken ? "Hide token" : "Show token"}>
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button variant="outline" size="icon" onClick={handleCopyToken} title="Copy token">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>}

            {/* Token Actions */}
            <div className="flex gap-2">
              {!hasActiveToken ? <Button onClick={handleGenerateToken} disabled={tokenMutation.isPending} variant="outline">
                  {tokenMutation.isPending ? <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Generating...
                    </> : <>
                      <Key className="h-4 w-4 mr-2" />
                      Generate Token
                    </>}
                </Button> : <>
                  <Button onClick={handleRegenerateToken} disabled={tokenMutation.isPending} variant="outline">
                    {tokenMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Regenerate
                  </Button>
                  <Button onClick={handleRevokeToken} disabled={tokenMutation.isPending} variant="destructive">
                    <XCircle className="h-4 w-4 mr-2" />
                    Revoke
                  </Button>
                </>}
            </div>
          </div>

          {/* Test Section */}
          <Separator />
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium">Test Endpoint</h4>
              <p className="text-sm text-muted-foreground">
                Send test data from a device to verify your endpoint is working correctly. This works regardless of the "Enable Endpoint" setting.
              </p>
            </div>

            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label htmlFor="test-device">Select Device</Label>
                <Select value={selectedDeviceId} onValueChange={setSelectedDeviceId} disabled={devicesLoading || !organizationDevices?.length}>
                  <SelectTrigger id="test-device">
                    <SelectValue placeholder={devicesLoading ? "Loading devices..." : organizationDevices?.length === 0 ? "No devices available" : "Select a device"} />
                  </SelectTrigger>
                  <SelectContent>
                    {organizationDevices?.map(device => <SelectItem key={device.device_id} value={device.device_id}>
                        {device.device_name} ({device.device_id})
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={handleTestEndpoint} disabled={!selectedDeviceId || !endpointUrl.trim() || testEndpointMutation.isPending} variant="outline" className="min-w-24">
                {testEndpointMutation.isPending ? <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Testing...
                  </> : <>
                    <PlayCircle className="h-4 w-4 mr-2" />
                    Test
                  </>}
              </Button>
            </div>

            {!endpointUrl.trim() && <p className="text-sm text-muted-foreground">
                Configure an endpoint URL above to enable testing.
              </p>}
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmAction !== null} onOpenChange={() => setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === 'regenerate' ? 'Regenerate Token?' : 'Revoke Token?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'regenerate' ? 'This will invalidate the current token and generate a new one. Any systems using the current token will need to be updated.' : 'This will permanently invalidate the current token. Webhook calls will no longer include authentication until a new token is generated.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmTokenAction}>
              {confirmAction === 'regenerate' ? 'Regenerate' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>;
};